import React, { useState, useRef, useEffect } from 'react';
import Button from './Button';

const VideoModal = ({ videoId = 'your-video-id', thumbWidth = 768, thumbHeight = 432, title = 'Watch Demo' }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const videoRef = useRef(null);
  const modalRef = useRef(null);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        closeModal();
      }
    };

    if (modalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [modalOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };

    if (modalOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [modalOpen]);

  const openModal = (e) => {
    e.preventDefault();
    setModalOpen(true);
    document.body.classList.add('modal-open'); // Prevent background scrolling
  };

  const closeModal = () => {
    setModalOpen(false);
    document.body.classList.remove('modal-open');
    
    // Reset video if it exists
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  return (
    <>
      <div>
        <Button 
          onClick={openModal} 
          variant="secondary"
          className="group"
        >
          <svg className="mr-2 h-4 w-4 fill-current text-indigo-300" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 7.5a.5.5 0 0 1 0 1v-1Zm-14 1a.5.5 0 0 1 0-1v1Zm14-1H1v1h14v-1Z" />
            <path d="M8.5 15a.5.5 0 0 1-1 0h1Zm-1-14a.5.5 0 0 1 1 0H7.5Zm0 14V1h1v14h-1Z" />
          </svg>
          <span>{title}</span>
        </Button>
      </div>

      {/* Modal backdrop */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm transition-opacity">
          {/* Modal dialog */}
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4 sm:px-6">
            <div 
              ref={modalRef}
              className="max-w-5xl mx-auto overflow-hidden rounded-lg shadow-xl"
            >
              {/* Modal content */}
              <div className="relative bg-gray-900/90 backdrop-blur-md p-6 border border-gray-800/50">
                {/* Close button */}
                <button 
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-200 transition-colors duration-200"
                  onClick={closeModal}
                >
                  <svg className="h-6 w-6 fill-current" viewBox="0 0 24 24">
                    <path d="M16.51 8.51l-4.5 4.5 4.5 4.5-1.5 1.5-4.5-4.5-4.5 4.5-1.5-1.5 4.5-4.5-4.5-4.5 1.5-1.5 4.5 4.5 4.5-4.5z" />
                  </svg>
                </button>
                
                {/* Video embed - replace with your video source */}
                <div className="aspect-w-16 aspect-h-9">
                  <iframe 
                    ref={videoRef}
                    className="absolute w-full h-full"
                    src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
                    title="Video"
                    allowFullScreen
                    allow="autoplay; encrypted-media"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VideoModal;
